/**
 * Client-side article state (readMap / unlocks) caching + merge rules.
 *
 * The per-article read docs and the unlock map are per USER and per EXAM
 * (`apps/{APP}/users/{uid}/articles`). Any local copy must be scoped the same
 * way: a single global cache let one account's (or exam's) reads and unlocks
 * leak into another's, because article ids repeat across exams and the
 * listener merged snapshots as a union with whatever was cached.
 *
 * Pure: apps own the storage and the listeners.
 */

/**
 * Storage key for one user's article state in one exam. null when either part
 * is missing — callers must not cache unscoped state.
 *
 * @param {string|null|undefined} uid      effective uid (ghost uid in ghost mode)
 * @param {string|null|undefined} appName  Firestore app doc (e.g. 'CC', 'CISSP')
 * @returns {string|null}
 */
export function articleStateCacheKey(uid, appName) {
  if (!uid || !appName) return null;
  return `articlesData:${appName}:${uid}`;
}

/** Prefix shared by every scoped key (for sign-out sweeps). */
export const ARTICLE_STATE_CACHE_PREFIX = 'articlesData:';

/**
 * Merge a listener snapshot's map (readMap or unlocks) over the local one.
 *
 * Both maps are grow-only, so local-only entries survive (an optimistic read or
 * unlock the snapshot hasn't caught up with must never disappear). For ids the
 * snapshot HAS, the snapshot wins — it is the server truth, and a stale local
 * copy (e.g. an older read_count, or another account's doc) must not mask it.
 *
 * @param {Object|null|undefined} snapshotMap
 * @param {Object|null|undefined} localMap
 * @returns {Object}
 */
export function mergeSnapshotOverLocal(snapshotMap, localMap) {
  return { ...(localMap ?? {}), ...(snapshotMap ?? {}) };
}
